import React, { memo } from "react";
import { Box, Flex, Image as ChakraImage, Tooltip } from "@chakra-ui/react";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { a } from "../appColors";

/**
 * The pictures sitting in an answer, above the writing (Figma 222:475).
 *
 * Each is a small card with a cross to take it out again. It is deliberately
 * a strip rather than the pictures themselves at full size: the answer stays
 * readable, and opening one is the canvas's job.
 */
const AttachmentChip = memo(({ attachment, readOnly, onRemove, onOpen }) => {
  const src = attachment.image?.src || attachment.url;
  const label = attachment.label || attachment.name || "Attached image";

  return (
    <Box position="relative" flexShrink={0}>
      <Tooltip label={label} placement="top" hasArrow fontSize="11px" openDelay={400}>
        <Box
          as="button"
          type="button"
          w="55px"
          h="51px"
          borderRadius="8px"
          border="1px solid"
          borderColor={a.text}
          overflow="hidden"
          bg={a.surface}
          display="block"
          cursor={onOpen ? "pointer" : "default"}
          aria-label={`Open ${label}`}
          onClick={() => onOpen?.(attachment)}
        >
          <ChakraImage src={src} alt="" w="100%" h="100%" objectFit="cover" draggable={false} />
        </Box>
      </Tooltip>

      {!readOnly && (
        <Box
          as="button"
          type="button"
          aria-label={`Remove ${label}`}
          position="absolute"
          top="-6px"
          right="-6px"
          color={a.text}
          bg={a.surface}
          borderRadius="full"
          lineHeight="0"
          cursor="pointer"
          _hover={{ color: a.accent }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(attachment.id);
          }}
        >
          <XCircleIcon style={{ width: 17, height: 17 }} />
        </Box>
      )}
    </Box>
  );
});
AttachmentChip.displayName = "AttachmentChip";

const AttachmentStrip = ({ attachments, readOnly, onRemove, onOpen }) => {
  if (!attachments.length) return null;
  return (
    <Flex gap="12px" align="center" flexWrap="wrap" px="19px" pt="13px">
      {attachments.map((attachment) => (
        <AttachmentChip
          key={attachment.id}
          attachment={attachment}
          readOnly={readOnly}
          onRemove={onRemove}
          onOpen={onOpen}
        />
      ))}
    </Flex>
  );
};

export default memo(AttachmentStrip);
